import { useMemo, useState } from "react";
import { Tabs } from "@design-system/Tabs/Tabs";
import styles from "./FinancialDocumentXlsxPreview.module.scss";
import { MAX_XLSX_PREVIEW_ROWS, MAX_XLSX_PREVIEW_COLS } from "./constants";

interface Props {
    sheetsHtml: Record<string, string>;
    sheetNames: string[];
    sheetsLimited: Record<string, { rows: boolean; cols: boolean }>;
    hasUnevaluatedFormulas?: boolean;
}

export const FinancialDocumentXlsxPreview: React.FC<Props> = ({
    sheetsHtml,
    sheetNames,
    sheetsLimited,
    hasUnevaluatedFormulas,
}) => {
    const [selectedSheet, setSelectedSheet] = useState(sheetNames[0]);
    const currentLimit = sheetsLimited[selectedSheet];
    const isLimited = currentLimit?.rows || currentLimit?.cols;

    const limitMessage = useMemo(() => {
        if (currentLimit?.rows && currentLimit?.cols) {
            return `Preview limited to first ${MAX_XLSX_PREVIEW_ROWS} rows and ${MAX_XLSX_PREVIEW_COLS} columns`;
        }
        if (currentLimit?.rows) {
            return `Preview limited to first ${MAX_XLSX_PREVIEW_ROWS} rows`;
        }
        if (currentLimit?.cols) {
            return `Preview limited to first ${MAX_XLSX_PREVIEW_COLS} columns`;
        }
        return null;
    }, [currentLimit]);

    if (sheetNames.length === 0) {
        return null;
    }

    return (
        <div className={styles.container}>
            <Tabs.Root
                value={selectedSheet}
                onValueChange={(v) => {
                    if (v != null) {
                        setSelectedSheet(String(v));
                    }
                }}
            >
                {sheetNames.length > 1 && (
                    <Tabs.List className={styles.sheetTabs}>
                        {sheetNames.map((name) => (
                            <Tabs.Tab key={name} value={name}>
                                {name}
                            </Tabs.Tab>
                        ))}
                    </Tabs.List>
                )}
                <div
                    className={styles.tableContainer}
                    dangerouslySetInnerHTML={{
                        __html: sheetsHtml[selectedSheet],
                    }}
                />
            </Tabs.Root>
            {hasUnevaluatedFormulas && (
                <div className={styles.formulaBanner}>
                    Contains unevaluated formula cells. To see
                    &ldquo;&hellip;&rdquo; values, open the file in Excel, save
                    it, then re-upload.
                </div>
            )}
            {isLimited && (
                <div className={styles.limitBanner}>{limitMessage}</div>
            )}
        </div>
    );
};
